import React, { useEffect, useState } from "react";
import { Container, Table, Badge, Card, CardBody, Input } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import LoadingSpinner from "../../components/LoadingSpinner";
import { GetDeliveryHistory, GetStores } from "../../requests";
import { toast } from "react-toastify";

const DeliveryHistory = () => {
  document.title = "Delivery History | Susipizza";

  const userRole = useSelector((state) => state?.login?.role);

  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [storesData, setStoresData] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(""); // "" = All

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const resp = await GetDeliveryHistory(token);
      setHistoryData(resp.data || []);
    } catch (err) {
      console.error("Failed to fetch delivery history:", err);
      toast.error("Failed to fetch delivery history logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    try {
      const resp = await GetStores();
      setStoresData(resp.data?.data || resp.data || []);
    } catch (err) {
      console.error("Failed to fetch stores:", err);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchStores();
  }, []);

  // Store admin's own data is already store-scoped server-side — this just
  // locks the dropdown to reflect that instead of leaving it on "All",
  // which would misleadingly suggest they're seeing every store's history.
  useEffect(() => {
    if (userRole === "admin") {
      const myStoreId = localStorage.getItem("storeId");
      if (myStoreId) {
        setSelectedStoreId(myStoreId);
      }
    }
  }, [userRole]);

  const storeNameById = (storeId) => {
    const store = storesData.find((s) => (s.storeId || s.id) === storeId);
    return store ? store.name : storeId || "—";
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString([], {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateTotalTime = (startStr, endStr) => {
    if (!startStr || !endStr) return "-";
    const start = new Date(startStr);
    const end = new Date(endStr);
    const diffMs = end - start;
    if (diffMs < 0) return "-";
    const diffMins = Math.floor(diffMs / 1000 / 60);
    if (diffMins < 60) {
      return `${diffMins} mins`;
    }
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours} hr ${mins} mins`;
  };

  // Filter history
  const filteredHistory = historyData.filter((log) => {
    if (selectedStoreId && log.storeId !== selectedStoreId) {
      return false;
    }
    const term = searchQuery.toLowerCase();
    return (
      (log.order_custom_id || "").toLowerCase().includes(term) ||
      (log.rider_name || "").toLowerCase().includes(term) ||
      (log.status || "").toLowerCase().includes(term)
    );
  });

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Delivery Management" breadcrumbItem="Delivery History" />

          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div>
              <h4>Delivery Fulfillment History</h4>
              <p className="text-muted m-0">View timestamps, total elapsed delivery times, and completion states.</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Input
                type="select"
                style={{ maxWidth: "220px" }}
                value={selectedStoreId}
                disabled={userRole === "admin"}
                onChange={(e) => setSelectedStoreId(e.target.value)}
              >
                <option value="">All Stores</option>
                {storesData.map((store) => (
                  <option key={store.id} value={store.storeId || store.id}>
                    {store.name}
                  </option>
                ))}
              </Input>
              <div style={{ maxWidth: "320px", width: "100%" }}>
                <input
                  type="text"
                  placeholder="Search history by Rider or Order ID..."
                  className="form-control"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          <Card className="border-0 shadow-sm">
            <CardBody>
              <div className="table-responsive">
                <Table className="table align-middle table-nowrap mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Order ID</th>
                      {userRole === "superadmin" && <th>Store</th>}
                      <th>Rider Name</th>
                      <th>Assigned At</th>
                      <th>Picked At</th>
                      <th>Delivered At</th>
                      <th>Total Duration</th>
                      <th>Delivery Fee</th>
                      <th>Fulfillment Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.length > 0 ? (
                      filteredHistory.map((log) => (
                        <tr key={log.id}>
                          <td className="font-weight-bold">{log.order_custom_id || "N/A"}</td>
                          {userRole === "superadmin" && <td>{storeNameById(log.storeId)}</td>}
                          <td>{log.rider_name}</td>
                          <td>{formatTime(log.assigned_at)}</td>
                          <td>{formatTime(log.picked_at)}</td>
                          <td>{formatTime(log.delivered_at)}</td>
                          <td>
                            <Badge color="light" className="text-dark border px-2 py-1">
                              {calculateTotalTime(log.assigned_at, log.delivered_at)}
                            </Badge>
                          </td>
                          <td>₹{log.delivery_charge || 0}</td>
                          <td>
                            <Badge
                              color={
                                log.status === "Delivered"
                                  ? "success"
                                  : log.status === "Out for Delivery"
                                  ? "warning"
                                  : log.status === "Cancelled"
                                  ? "danger"
                                  : "secondary"
                              }
                              className="text-uppercase"
                            >
                              {log.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={userRole === "superadmin" ? 9 : 8} className="text-center text-muted p-4">
                          No delivery history records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DeliveryHistory;
